// src/schemas/api/v2/responses/meta.ts
//
// V2 JSON wire-format schemas for meta endpoints (status, version, locales).
// These return simple flat objects — no record/details envelope.
//
// V2 includes `success: true` in responses. V3 omits success (uses HTTP status codes).

import { z } from 'zod';

export const systemStatusResponseSchema = z.object({
  success: z.boolean(),
  status: z.string(),
  locale: z.string(),
});

/**
 * Public /status page payload (GET /api/v2/status/metrics).
 *
 * SAFE aggregates only — mirrors the backend contract, which by construction
 * exposes no admin/PII fields: per-dependency health is a plain up/down
 * boolean (no versions, hosts or connection details), plus the lifetime and
 * daily "secrets created" demand series and the public MAJOR.MINOR.PATCH.
 */
export const statusMetricPointSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const systemStatusMetricsResponseSchema = z.object({
  success: z.boolean(),
  status: z.enum(['nominal', 'degraded']),
  checks: z.object({
    datastore: z.boolean(),
    database: z.boolean(),
  }),
  metrics: z.object({
    secrets_created_total: z.number(),
    secrets_created_daily: z.array(statusMetricPointSchema),
    days: z.number(),
  }),
  version: z.string(),
  locale: z.string(),
});

export const systemVersionResponseSchema = z.object({
  success: z.boolean(),
  version: z.array(z.union([z.string(), z.number()])),
  locale: z.string(),
});

export const supportedLocalesResponseSchema = z.object({
  success: z.boolean(),
  locales: z.array(z.string()),
  default_locale: z.string(),
  locale: z.string(),
});

export type StatusMetricPoint = z.infer<typeof statusMetricPointSchema>;
export type SystemStatusResponse = z.infer<typeof systemStatusResponseSchema>;
export type SystemStatusMetricsResponse = z.infer<
  typeof systemStatusMetricsResponseSchema
>;
export type SystemVersionResponse = z.infer<typeof systemVersionResponseSchema>;
export type SupportedLocalesResponse = z.infer<typeof supportedLocalesResponseSchema>;
